import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import DashboardTodoWidget from '../components/dashboard/DashboardTodoWidget';
import PlanningSummaryWidget from '../components/dashboard/PlanningSummaryWidget';
import EmployeePlanningWidget from '../components/dashboard/EmployeePlanningWidget';
import DPDAlertsWidget from '../components/dashboard/DPDAlertsWidget';
import { DashboardCardBoard, DashCard } from '../components/dashboard/DashboardCardBoard';
import EmploymentChangesWidget from '../components/dashboard/EmploymentChangesWidget';
import SelfRouteReportButton from '../components/SelfRouteReportButton';
import TodayAssignmentCard from '../components/dashboard/TodayAssignmentCard';
import { FuelFormCard } from '../components/dashboard/FuelFormCard';
import RitmonitorLiveCard from '../components/dashboard/RitmonitorLiveCard';
import MyRoutes from './MyRoutes';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, 
  Building2, 
  Clock, 
  CheckCircle,
  XCircle,
  Calendar,
  Briefcase,
  FileText,
  AlertCircle,
  Loader2,
  TrendingUp,
  BarChart3,
  PieChart as PieChartIcon,
  RefreshCw,
  Archive,
  UserCheck,
  Heart,
  Coffee,
  Baby,
  PlusCircle,
  ArrowRight,
  Car,
  AlertTriangle,
  Shield,
  Wrench,
  FileCheck,
  IdCard
} from 'lucide-react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { cn } from '../lib/utils';

// Chart colors
const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#6366f1', '#8b5cf6'];
const STATUS_COLORS = {
  PENDING: '#f59e0b',
  APPROVED: '#10b981',
  REJECTED: '#ef4444',
  CANCELLED: '#64748b',
  ARCHIVED: '#8b5cf6'
};

// Daily Status config for modal
const DAILY_STATUS_CONFIG = {
  WORKING: { color: '#10B981', bgClass: 'bg-emerald-500', icon: CheckCircle, label: { tr: 'Çalıştı', en: 'Working', nl: 'Gewerkt' } },
  HALF_DAY: { color: '#F59E0B', bgClass: 'bg-amber-400', icon: Clock, label: { tr: 'Yarım Gün', en: 'Half Day', nl: 'Halve Dag' } },
  SICK: { color: '#EF4444', bgClass: 'bg-red-500', icon: Heart, label: { tr: 'Hasta', en: 'Sick', nl: 'Ziek' } },
  DOP: { color: '#3B82F6', bgClass: 'bg-blue-500', icon: Coffee, label: { tr: 'DOP', en: 'Day Off', nl: 'Vrije Dag' } },
  ON_LEAVE: { color: '#F97316', bgClass: 'bg-orange-500', icon: Briefcase, label: { tr: 'İzinli', en: 'On Leave', nl: 'Met Verlof' } },
  PATERNITY: { color: '#8B5CF6', bgClass: 'bg-purple-500', icon: Baby, label: { tr: 'Babalık İzni', en: 'Paternity', nl: 'Vaderschapsverlof' } }
};

// Month names
const getMonthNames = (t) => [
  t('months.jan'), t('months.feb'), t('months.mar'), t('months.apr'),
  t('months.may'), t('months.jun'), t('months.jul'), t('months.aug'),
  t('months.sep'), t('months.oct'), t('months.nov'), t('months.dec')
];

export const Dashboard = () => {
  const { t, language } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFirm, setSelectedFirm] = useState('all');
  const [dashboardData, setDashboardData] = useState(null);
  const [firms, setFirms] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [todayUsers, setTodayUsers] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [myTodayStatus, setMyTodayStatus] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [myWorkStats, setMyWorkStats] = useState(null);
  const [vehicleWarnings, setVehicleWarnings] = useState({ vehicles: [], summary: { total: 0, with_warnings: 0, critical: 0 } });
  const [expiringDocuments, setExpiringDocuments] = useState({ profiles: [], count: 0 });
  const [sendingExpiryAlerts, setSendingExpiryAlerts] = useState(false);
  const [lastExpiryCheck, setLastExpiryCheck] = useState(null);
  
  const monthNames = getMonthNames(t);

  // Prepare monthly work status chart data (for all users)
  const monthlyWorkChartData = useMemo(() => {
    if (!myWorkStats?.monthly_breakdown) return [];
    
    const months = Object.keys(myWorkStats.monthly_breakdown).sort();
    return months.map(monthKey => {
      const monthData = myWorkStats.monthly_breakdown[monthKey];
      const monthIndex = parseInt(monthKey.split('-')[1]) - 1;
      return {
        name: monthNames[monthIndex] || monthKey,
        WORKING: monthData.WORKING || 0,
        HALF_DAY: monthData.HALF_DAY || 0,
        SICK: monthData.SICK || 0,
        DOP: monthData.DOP || 0,
        ON_LEAVE: monthData.ON_LEAVE || 0
      };
    });
  }, [myWorkStats, monthNames]);

  // Work Status Tooltip Component
  const WorkStatusTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
          <p className="font-medium text-slate-900 dark:text-slate-100 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }}></div>
              <span className="text-slate-600 dark:text-slate-300">{entry.name}:</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{entry.value} {t('leave.day') || 'gün'}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const isAnyAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN';

  useEffect(() => {
    // Mart 01, 2026 — SUBCO için ayrı akış (fetchDashboardData çağrılmaz)
    if (user?.role === 'SUBCO') return;
    fetchDashboardData();
    if (isAnyAdmin) {
      fetchFirms();
      fetchTodayStatus();
      fetchVehicleWarnings();
      fetchExpiringDocuments();
    }
    // Fetch current user's today status (for all roles)
    fetchMyTodayStatus();
    // Fetch recent leave requests for all users
    fetchRecentRequests();
    // Fetch my work stats for monthly chart
    fetchMyWorkStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedFirm, user?.role]);

  const fetchExpiringDocuments = async () => {
    try {
      const response = await api.get('/employee-profile/expiring-documents?days=90');
      if (response.data.success) {
        setExpiringDocuments(response.data.data);
        setLastExpiryCheck(new Date().toISOString());
      }
    } catch (error) {
      console.error('Failed to fetch expiring documents', error);
    }
  };

  const handleSendExpiryAlerts = async () => {
    try {
      setSendingExpiryAlerts(true);
      const response = await api.post('/admin/expiry-notifications/run-now');
      if (response.data.success) {
        const count = response.data.data?.notifications || 0;
        const checked = response.data.data?.profiles_checked || 0;
        if (count > 0) {
          toast.success(`${count} bildirim gönderildi (${checked} çalışan profili tarandı)`);
        } else {
          toast.info(`Şu anda bildirilecek belge yok — ${checked} profil tarandı, 14/7 gün eşiğinde belge bulunamadı.`);
        }
        await fetchExpiringDocuments();
      }
    } catch (error) {
      console.error('Failed to send expiry alerts', error);
      toast.error(t('dashboard.sendAlertError') || 'Bildirim gönderme başarısız oldu');
    } finally {
      setSendingExpiryAlerts(false);
    }
  };

  const fetchVehicleWarnings = async () => {
    try {
      let endpoint = '/vehicles';
      if (isAnyAdmin && selectedFirm !== 'all') {
        endpoint += `?firm_id=${selectedFirm}`;
      }
      const response = await api.get(endpoint);
      if (response.data.success) {
        // Filter only vehicles with warnings
        const allVehicles = response.data.data || [];
        const vehiclesWithWarnings = allVehicles.filter(v => v.has_warnings);
        setVehicleWarnings({
          vehicles: vehiclesWithWarnings,
          summary: response.data.summary || { total: 0, with_warnings: 0, critical: 0 }
        });
      }
    } catch (error) {
      console.error('Failed to fetch vehicle warnings', error);
    }
  };

  const fetchMyWorkStats = async () => {
    try {
      const response = await api.get(`/daily-status/my-statistics?year=${selectedYear}`);
      if (response.data.success) {
        setMyWorkStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch my work stats', error);
    }
  };

  const fetchRecentRequests = async () => {
    try {
      const response = await api.get('/leave/requests?limit=5');
      if (response.data.success) {
        setRecentRequests(response.data.data?.slice(0, 5) || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent requests', error);
    }
  };

  const fetchMyTodayStatus = async () => {
    try {
      const response = await api.get('/daily-status/my-today');
      if (response.data.success) {
        setMyTodayStatus(response.data.data.status);
      }
    } catch (error) {
      console.error('Failed to fetch my today status', error);
      setMyTodayStatus('NONE');
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch firms', error);
    }
  };

  const fetchTodayStatus = async () => {
    try {
      let endpoint = '/daily-status/today';
      if (isAnyAdmin && selectedFirm !== 'all') {
        endpoint += `?firm_id=${selectedFirm}`;
      }
      const response = await api.get(endpoint);
      if (response.data.success) {
        setTodayStatus(response.data.data);
      }
      
      // Also fetch today's users for modal
      const today = new Date().toISOString().split('T')[0];
      let usersEndpoint = `/daily-status?date=${today}`;
      if (isAnyAdmin && selectedFirm !== 'all') {
        usersEndpoint += `&firm_id=${selectedFirm}`;
      }
      const usersResponse = await api.get(usersEndpoint);
      if (usersResponse.data.success) {
        setTodayUsers(usersResponse.data.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch today status', error);
    }
  };

  // Open status modal
  const openStatusModal = (status) => {
    setSelectedStatus(status);
    setStatusModalOpen(true);
  };

  // Filter users by status for modal
  const modalUsers = useMemo(() => {
    if (!selectedStatus) return [];
    return todayUsers
      .filter(u => u.status === selectedStatus)
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'tr'));
  }, [todayUsers, selectedStatus]);

  // Get status label
  const getStatusLabel = (status) => {
    return DAILY_STATUS_CONFIG[status]?.label?.[language] || status;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      if (user?.role === 'EMPLOYEE') {
        endpoint = `/me/dashboard/summary?year=${selectedYear}`;
      } else {
        endpoint = `/admin/dashboard/summary?year=${selectedYear}`;
        if (isAnyAdmin && selectedFirm !== 'all') {
          endpoint += `&firm_id=${selectedFirm}`;
        }
      }
      
      const response = await api.get(endpoint);
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color = 'slate', subtext, onClick }) => {
    const colorConfig = {
      blue: { iconBg: 'bg-blue-500', border: 'border-l-blue-500', shadow: 'shadow-blue-500/10' },
      emerald: { iconBg: 'bg-emerald-500', border: 'border-l-emerald-500', shadow: 'shadow-emerald-500/10' },
      amber: { iconBg: 'bg-amber-500', border: 'border-l-amber-500', shadow: 'shadow-amber-500/10' },
      purple: { iconBg: 'bg-purple-500', border: 'border-l-purple-500', shadow: 'shadow-purple-500/10' },
      red: { iconBg: 'bg-red-500', border: 'border-l-red-500', shadow: 'shadow-red-500/10' },
      cyan: { iconBg: 'bg-cyan-500', border: 'border-l-cyan-500', shadow: 'shadow-cyan-500/10' },
      slate: { iconBg: 'bg-slate-500', border: 'border-l-slate-400', shadow: 'shadow-slate-500/10' }
    };
    const cfg = colorConfig[color] || colorConfig.slate;
    
    return (
      <div 
        className={cn(
          "bg-white dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700/60 rounded-xl p-5 border-l-[3px] transition-all duration-200",
          cfg.border,
          onClick && "cursor-pointer hover:shadow-lg hover:-translate-y-0.5"
        )}
        onClick={onClick}
        data-testid={`stat-card-${label?.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider truncate">{label}</p>
            <p className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {value ?? '-'}
            </p>
            {subtext && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{subtext}</p>}
          </div>
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm", cfg.iconBg, cfg.shadow)}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </div>
    );
  };


  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value} {t('leave.days')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    // Mart 01, 2026 — SUBCO için loading'i atla (fetchDashboardData çağrılmıyor)
    if (user?.role !== 'SUBCO') {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      );
    }
  }

  // Mart 01, 2026 — SUBCO Dashboard: Sadece Planlamalarım listesi (sadeleştirme)
  if (user?.role === 'SUBCO') {
    return <MyRoutes />;
  }

  // Employee Dashboard
  if (user?.role === 'EMPLOYEE') {
    return (
      <div className="space-y-8 animate-fade-in overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('common.dashboard')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{t('common.welcome')}, {user?.full_name}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Şub 16, 2026 — Turu Bildir butonu yılın yanında (yalnızca EMPLOYEE, feature flag ile) */}
            <SelfRouteReportButton compact />
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchDashboardData} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Bugünkü/yarınki atama belirgin kart (Faz 2) */}
        <FuelFormCard />
        <TodayAssignmentCard />

        {/* Planning widget — shows up only when the user actually has
            assignments (widget renders null otherwise). */}
        <EmployeePlanningWidget />

        {/* Top Cards - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Quick Action: Create Leave Request */}
          <div 
            onClick={() => navigate('/dashboard/leave-requests/create')}
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-[1.01]"
            data-testid="create-leave-request-banner"
          >
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <PlusCircle className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('leave.createRequest') }
                  </h3>
                  <p className="text-white/80 text-sm mt-0.5">
                    {t('dashboard.createLeaveHint') }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <ArrowRight className="h-5 w-5 text-white group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-white/30 rounded-full animate-pulse" />
            <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-white/20 rounded-full animate-pulse delay-300" />
          </div>

          {/* Today's Status Card */}
          {myTodayStatus && myTodayStatus !== 'NONE' && DAILY_STATUS_CONFIG[myTodayStatus] ? (
            <div 
              className={cn(
                "relative overflow-hidden rounded-2xl p-6 text-white shadow-lg",
                DAILY_STATUS_CONFIG[myTodayStatus].bgClass
              )}
              data-testid="today-status-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  {(() => {
                    const StatusIcon = DAILY_STATUS_CONFIG[myTodayStatus].icon;
                    return <StatusIcon className="h-7 w-7 text-white" />;
                  })()}
                </div>
                <div>
                  <p className="text-white/80 text-sm font-medium">
                    {t('dashboard.todayStatus') }
                  </p>
                  <h3 className="text-2xl font-bold">
                    {DAILY_STATUS_CONFIG[myTodayStatus].label[language] || DAILY_STATUS_CONFIG[myTodayStatus].label.tr}
                  </h3>
                </div>
              </div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full" />
              <div className="absolute -right-2 -bottom-2 w-20 h-20 bg-white/10 rounded-full" />
            </div>
          ) : (
            <div 
              className="relative overflow-hidden rounded-2xl p-6 text-white shadow-lg bg-gradient-to-r from-slate-500 to-slate-600"
              data-testid="today-status-card-empty"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Clock className="h-7 w-7 text-white" />
                </div>
                <div>
                  <p className="text-white/80 text-sm font-medium">
                    {t('dashboard.todayStatus') }
                  </p>
                  <h3 className="text-2xl font-bold">
                    {t('dashboard.noStatusYet') || 'Henüz belirlenmedi'}
                  </h3>
                </div>
              </div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full" />
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard 
            icon={Clock} 
            label={t('dashboard.pendingRequests')} 
            value={dashboardData?.request_counts?.PENDING || 0}
            color="amber"
          />
          <StatCard 
            icon={CheckCircle} 
            label={t('dashboard.approvedRequests')} 
            value={dashboardData?.request_counts?.APPROVED || 0}
            color="emerald"
          />
          <StatCard 
            icon={XCircle} 
            label={t('dashboard.rejectedRequests')} 
            value={dashboardData?.request_counts?.REJECTED || 0}
            color="red"
          />
        </div>

        {/* Çalışanlara izin sayıları gösterilmez — sadece aylık çalışma durumu */}
        <div className="grid grid-cols-1 gap-6">
          {/* Monthly Work Status Chart */}
          <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('dashboard.monthlyWorkStatus')}
              </h2>
            </div>
            <div className="h-64 min-h-[256px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={monthlyWorkChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip content={<WorkStatusTooltip />} />
                  <Bar dataKey="WORKING" stackId="a" fill="#10B981" name={t('dashboard.working')} />
                  <Bar dataKey="HALF_DAY" stackId="a" fill="#F59E0B" name={t('dashboard.halfDay')} />
                  <Bar dataKey="SICK" stackId="a" fill="#EF4444" name={t('dashboard.sick')} />
                  <Bar dataKey="DOP" stackId="a" fill="#3B82F6" name={t('dashboard.dop')} />
                  <Bar dataKey="ON_LEAVE" stackId="a" fill="#F97316" name={t('dashboard.onLeave')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Recent Leave Requests - Enhanced */}
        <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="recent-requests-section">
          <div className="p-6 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <Clock className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('dashboard.myRecentRequests') }
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.lastFiveRequests') || 'Son 5 talebiniz'}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard/leave-requests')}
                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
              >
                {t('dashboard.viewAll') }
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
          
          {recentRequests.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {recentRequests.map((req, index) => {
                const statusConfig = {
                  PENDING: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', icon: Clock, iconColor: 'text-amber-500' },
                  APPROVED: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', icon: CheckCircle, iconColor: 'text-emerald-500' },
                  REJECTED: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800', icon: XCircle, iconColor: 'text-red-500' },
                  CANCELLED: { bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700', icon: Archive, iconColor: 'text-slate-400' }
                };
                const config = statusConfig[req.status] || statusConfig.PENDING;
                const StatusIcon = config.icon;
                
                return (
                  <div 
                    key={req.id || index} 
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                    onClick={() => navigate('/dashboard/leave-requests')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={cn("p-2 rounded-lg", config.bg)}>
                          <StatusIcon className={cn("h-5 w-5", config.iconColor)} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-100">{req.leave_type_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {req.start_date} - {req.end_date}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                              {req.total_days} {t('leave.day')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn(config.bg, config.text, config.border, "font-medium")}>
                        {t(`leave.${req.status?.toLowerCase()}`) || req.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-slate-500 dark:text-slate-400 mb-4">{t('leave.noRequests') }</p>
              <Button 
                onClick={() => navigate('/dashboard/leave-requests/create')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                {t('leave.createRequest') }
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Admin Dashboard (Firm Admin or Super Admin)
  const monthlyChartData = dashboardData?.monthly_series?.map(item => ({
    name: monthNames[item.month - 1],
    approved_days: item.approved_days
  })) || [];

  const statusPieData = dashboardData?.status_distribution ? 
    Object.entries(dashboardData.status_distribution)
      .filter(([_, value]) => value > 0)
      .map(([status, value]) => ({
        name: t(`leave.${status.toLowerCase()}`),
        value,
        color: STATUS_COLORS[status]
      })) : [];

  const leaveTypeChartData = dashboardData?.leave_type_top?.map(item => ({
    name: item.leave_type_name,
    approved_days: item.approved_days
  })) || [];

  const firmComparisonData = dashboardData?.firm_comparison?.map(item => ({
    name: item.firm_name,
    approved_days: item.approved_days
  })) || [];

  // Soft color palette for charts
  const SOFT_COLORS = ['#818CF8', '#34D399', '#FBBF24', '#FB7185', '#38BDF8', '#A78BFA'];

  return (
    <div className="space-y-5 animate-fade-in overflow-x-hidden">
      {/* ─── Welcome Row ─── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-indigo-500 dark:text-indigo-400 tracking-wide uppercase">
            {new Date().toLocaleDateString(language === 'tr' ? 'tr-TR' : language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-0.5 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('common.welcome')}, {user?.first_name || user?.full_name}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FuelFormCard compact />
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[90px] h-8 text-xs rounded-lg border-slate-200 dark:border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAnyAdmin && (
            <Select value={selectedFirm} onValueChange={setSelectedFirm}>
              <SelectTrigger className="w-[130px] sm:w-[160px] h-8 text-xs rounded-lg border-slate-200 dark:border-slate-700">
                <SelectValue placeholder={t('admin.selectFirm')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('dashboard.allFirms')}</SelectItem>
                {firms.map(firm => (
                  <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="icon" onClick={fetchDashboardData} disabled={loading} className="h-8 w-8 text-slate-400 hover:text-slate-600">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* ─── Bugünkü Atama (varsa) — Şub 09, 2026: Admin de çalışansa,
           atama kartı en üstte görünür ki form'u hemen doldurabilsin.
           Atama yoksa widget kendini gizler, ek görsel kalabalık olmaz. */}
      <TodayAssignmentCard />

      {/* PostNL Ritmonitor canlı ilerleme (admin görür) — Haz 13, 2026 */}
      {isAnyAdmin && <RitmonitorLiveCard />}

      {/* ─── Compact KPI Cards ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-metrics-row">
        <div className="group bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-4 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_24px_rgba(99,102,241,0.08)]" onClick={() => navigate('/dashboard/users?status=ACTIVE')} data-testid="kpi-active-employees">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t('dashboard.activeEmployees')}</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
              <Users className="h-3.5 w-3.5 text-emerald-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{dashboardData?.employees_active_count || 0}</p>
          {isAnyAdmin && dashboardData?.is_global && (
            <p className="text-[10px] text-slate-400 mt-0.5">{dashboardData?.total_firms || 0} {t('admin.totalFirms').toLowerCase()}</p>
          )}
        </div>

        <div className="group bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-4 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_24px_rgba(245,158,11,0.08)]" onClick={() => navigate('/dashboard/admin/leave-requests?status=PENDING')} data-testid="kpi-pending-requests">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t('dashboard.pendingRequests')}</span>
            <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
              <Clock className="h-3.5 w-3.5 text-amber-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{dashboardData?.leave_requests_pending_count || 0}</p>
          {(dashboardData?.pending_registrations || 0) > 0 && (
            <p className="text-[10px] text-amber-500 mt-0.5">{dashboardData.pending_registrations} {t('dashboard.pendingRegistrations').toLowerCase()}</p>
          )}
        </div>

        <div className="group bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-4 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_24px_rgba(99,102,241,0.08)]" onClick={() => navigate('/dashboard/admin/leave-requests?status=APPROVED')} data-testid="kpi-approved-requests">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t('dashboard.approvedRequests')}</span>
            <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
              <CheckCircle className="h-3.5 w-3.5 text-indigo-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{dashboardData?.leave_requests_approved_count || 0}</p>
        </div>

        <div className="group bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-4 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_24px_rgba(99,102,241,0.08)]" onClick={() => navigate('/dashboard/admin/leave-requests')} data-testid="kpi-total-days">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t('dashboard.totalApprovedDays')}</span>
            <div className="w-7 h-7 rounded-lg bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center">
              <TrendingUp className="h-3.5 w-3.5 text-sky-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{dashboardData?.total_approved_days || 0}</p>
        </div>
      </div>

      {/* ─── Quick Actions (compact pills) ─── */}
      <div className="flex flex-wrap items-center gap-2" data-testid="quick-actions-row">
        <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mr-1">{t('dashboard.quickActions')}</span>
        <button onClick={() => navigate('/dashboard/admin/leave-requests')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors" data-testid="quick-action-leave">
          <Calendar className="h-3 w-3" /> {t('admin.leaveRequestManagement')}
        </button>
        <button onClick={() => navigate('/dashboard/users?status=PENDING')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors" data-testid="quick-action-pending">
          <Clock className="h-3 w-3" /> {t('admin.pendingApprovals')}
        </button>
        {isAnyAdmin && (
          <button onClick={() => navigate('/dashboard/firms')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/30 transition-colors" data-testid="quick-action-firms">
            <Building2 className="h-3 w-3" /> {t('admin.firmManagement')}
          </button>
        )}
        <button onClick={() => navigate('/dashboard/audit')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors" data-testid="quick-action-audit">
          <FileText className="h-3 w-3" /> {t('common.auditLogs')}
        </button>
      </div>

      {/* ─── Today Status (compact pill bar) ─── */}
      {todayStatus && (
        <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-4" data-testid="today-status-dashboard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('dashboard.todayWorking')}</span>
            </div>
            <span className="text-[11px] text-slate-400">{todayStatus.total_employees || 0} {t('dashboard.activeEmployees').toLowerCase()}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(DAILY_STATUS_CONFIG).map(([statusKey, config]) => {
              const count = statusKey === 'WORKING' ? (todayStatus.working || 0)
                : statusKey === 'HALF_DAY' ? (todayStatus.half_day || 0)
                : statusKey === 'SICK' ? (todayStatus.sick || 0)
                : statusKey === 'DOP' ? (todayStatus.dop || 0)
                : statusKey === 'ON_LEAVE' ? (todayStatus.on_leave || 0)
                : statusKey === 'PATERNITY' ? (todayStatus.paternity || 0) : 0;
              if (count === 0) return null;
              return (
                <button key={statusKey} onClick={() => openStatusModal(statusKey)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:shadow-sm hover:-translate-y-0.5 cursor-pointer" style={{ backgroundColor: config.color + '10', borderColor: config.color + '25', color: config.color }} data-testid={`today-status-${statusKey.toLowerCase()}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: config.color }} />
                  {config.label[language] || config.label.tr}: {count}
                </button>
              );
            })}
            {Object.entries(DAILY_STATUS_CONFIG).every(([statusKey]) => {
              const count = statusKey === 'WORKING' ? (todayStatus.working || 0) : statusKey === 'HALF_DAY' ? (todayStatus.half_day || 0) : statusKey === 'SICK' ? (todayStatus.sick || 0) : statusKey === 'DOP' ? (todayStatus.dop || 0) : statusKey === 'ON_LEAVE' ? (todayStatus.on_leave || 0) : statusKey === 'PATERNITY' ? (todayStatus.paternity || 0) : 0;
              return count === 0;
            }) && (
              <span className="text-xs text-slate-400">{t('dashboard.noStatusYet') || 'Henüz durum verisi yok'}</span>
            )}
          </div>
          {(() => {
            const total = todayStatus.total_employees || 1;
            const segments = [
              { key: 'WORKING', count: todayStatus.working || 0, color: '#10B981' },
              { key: 'HALF_DAY', count: todayStatus.half_day || 0, color: '#F59E0B' },
              { key: 'SICK', count: todayStatus.sick || 0, color: '#EF4444' },
              { key: 'DOP', count: todayStatus.dop || 0, color: '#3B82F6' },
              { key: 'ON_LEAVE', count: todayStatus.on_leave || 0, color: '#F97316' },
              { key: 'PATERNITY', count: todayStatus.paternity || 0, color: '#8B5CF6' },
            ].filter(s => s.count > 0);
            if (segments.length === 0) return null;
            return (
              <div className="mt-3 flex rounded-full h-1.5 overflow-hidden bg-slate-100 dark:bg-slate-700/50">
                {segments.map(seg => (
                  <div key={seg.key} className="h-full transition-all duration-700" style={{ width: `${(seg.count / total) * 100}%`, backgroundColor: seg.color }} />
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ─── Kart panosu: sıra + aç/kapa kullanıcıda saklanır ─── */}
      <EmployeePlanningWidget />
      <DashboardCardBoard
        storageKey={`dashboard.cards.${user?.id || 'anon'}`}
        defaultOrder={['vehicles', 'documents', 'employment', 'planning', 'todo', 'leave-trend', 'status-dist', 'leave-types', 'firm-compare', 'dpd']}
        defaultCollapsed={['dpd']}
      >
        {isAnyAdmin && (
          <DashCard id="employment" title="İşe Alınanlar / Ayrılanlar">
            <EmploymentChangesWidget />
          </DashCard>
        )}
        {isAnyAdmin && (
          <DashCard id="planning" title="Planlama Özeti" wide>
            <PlanningSummaryWidget year={selectedYear} />
          </DashCard>
        )}
        {isAnyAdmin && (
          <DashCard id="dpd" title="DPD Uyarıları" wide>
            <DPDAlertsWidget />
          </DashCard>
        )}
        <DashCard id="todo" title="Yapılacaklar" wide>
          <DashboardTodoWidget />
        </DashCard>
        {/* Monthly Leave Trend - AreaChart */}
        <DashCard id="leave-trend" title={t('dashboard.monthlyApprovedDays')}>
        <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-5 h-full" data-testid="chart-monthly-leave">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('dashboard.monthlyApprovedDays')}</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <AreaChart data={monthlyChartData}>
                <defs>
                  <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#818CF8" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#818CF8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={30} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="approved_days" stroke="#818CF8" strokeWidth={2} fill="url(#colorApproved)" name={t('dashboard.approvedDays')} dot={{ r: 3, fill: '#818CF8', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#6366F1', strokeWidth: 2, stroke: '#fff' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        </DashCard>

        {/* Status Distribution Donut */}
        <DashCard id="status-dist" title={t('dashboard.statusDistribution')}>
        <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-5 h-full" data-testid="chart-status-dist">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('dashboard.statusDistribution')}</h3>
          <div className="h-52">
            {statusPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <PieChart>
                  <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" labelLine={false} strokeWidth={2} stroke="#fff">
                    {statusPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SOFT_COLORS[index % SOFT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-400">{t('common.noData')}</div>
            )}
          </div>
        </div>
        </DashCard>

        {/* ─── Araç uyarıları — en belirgin kart ─── */}
        {isAnyAdmin && vehicleWarnings.summary.with_warnings > 0 && (
          <DashCard id="vehicles" title={t('dashboard.vehicleWarnings')} icon={Car} accent="text-rose-600 dark:text-rose-400">
          <div className="bg-rose-50/60 dark:bg-rose-950/30 rounded-2xl border-2 border-rose-300 dark:border-rose-800 p-5 h-full shadow-sm" data-testid="alerts-vehicles">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center"><Car className="h-4 w-4 text-white" /></span>
                <h3 className="text-base font-bold text-rose-700 dark:text-rose-300">{t('dashboard.vehicleWarnings')}</h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-600 text-white">{vehicleWarnings.summary.with_warnings}</span>
                {vehicleWarnings.summary.critical > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">{vehicleWarnings.summary.critical} {t('dashboard.critical')}</span>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/vehicles')} className="h-7 text-xs text-rose-600 hover:text-rose-700 px-2">
                {t('dashboard.viewAllVehicles')} <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
            <div className="space-y-2 max-h-[260px] overflow-y-auto sidebar-nav">
              {vehicleWarnings.vehicles.map((vehicle) => (
                <div key={vehicle.id} className="flex items-center justify-between p-2.5 rounded-xl bg-white dark:bg-slate-800/70 border border-rose-100 dark:border-rose-900/40 hover:border-rose-300 hover:shadow cursor-pointer transition-colors group" onClick={() => navigate(`/dashboard/vehicles/${vehicle.id}?tab=maintenance`)} data-testid={`vehicle-warning-${vehicle.id}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", vehicle.has_critical ? "bg-rose-50 dark:bg-rose-900/20" : "bg-amber-50 dark:bg-amber-900/20")}>
                      <Car className={cn("h-3.5 w-3.5", vehicle.has_critical ? "text-rose-500" : "text-amber-500")} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-200">{vehicle.license_plate}</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {vehicle.warnings?.slice(0, 2).map((warning, idx) => (
                          <span key={idx} className={cn("text-[10px] px-1.5 py-0.5 rounded-full", warning.level === 'critical' ? "bg-rose-50 text-rose-600 dark:bg-rose-900/20" : "bg-amber-50 text-amber-600 dark:bg-amber-900/20")}>
                            {warning.type === 'insurance' && <Shield className="h-2.5 w-2.5 inline mr-0.5" />}
                            {warning.type === 'inspection' && <FileCheck className="h-2.5 w-2.5 inline mr-0.5" />}
                            {warning.type === 'maintenance' && <Wrench className="h-2.5 w-2.5 inline mr-0.5" />}
                            {warning.message_key ? t(`dashboard.${warning.message_key === 'insurance_expired' ? 'insuranceExpired' : warning.message_key === 'insurance_expiring' ? 'insuranceExpiring' : warning.message_key === 'inspection_expired' ? 'inspectionExpired' : warning.message_key === 'inspection_expiring' ? 'inspectionExpiring' : warning.message_key === 'maintenance_overdue' ? 'maintenanceOverdue' : 'maintenanceUpcoming'}`).replace('{days}', Math.abs(warning.days)) : warning.message}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
          </DashCard>
        )}

        {isAnyAdmin && (
          <DashCard id="documents" title={t('employeeProfile.expiringDocuments')} icon={IdCard}>
          <div className={cn("bg-white dark:bg-slate-800/60 rounded-2xl border p-5 h-full", expiringDocuments.count > 0 ? "border-orange-100 dark:border-orange-800/30" : "border-slate-100 dark:border-slate-700/50")} data-testid="expiry-documents-widget">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <IdCard className={cn("h-4 w-4", expiringDocuments.count > 0 ? "text-orange-500" : "text-emerald-500")} />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('employeeProfile.expiringDocuments')}</h3>
                {expiringDocuments.count > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400">{expiringDocuments.count}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSendExpiryAlerts}
                  disabled={sendingExpiryAlerts}
                  className="h-7 text-[11px] text-orange-500 hover:text-orange-600 hover:bg-orange-50 px-2"
                  data-testid="send-expiry-alerts-btn"
                  title={t('dashboard.checkExpiryTooltip') || 'Süresi dolmakta olan belgeler için kontrol et ve bildirimleri tetikle'}
                >
                  {sendingExpiryAlerts ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
                  {sendingExpiryAlerts ? (t('dashboard.checking') || 'Kontrol ediliyor...') : (t('dashboard.sendAlert') || 'Uyarı Gönder')}
                </Button>
                <Button variant="ghost" size="icon" onClick={fetchExpiringDocuments} className="h-7 w-7 text-slate-400" data-testid="refresh-expiry-btn" title={t('common.refresh') || 'Listeyi yenile'}>
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {expiringDocuments.count > 0 ? (
              <div className="space-y-2 max-h-[200px] overflow-y-auto sidebar-nav">
                {expiringDocuments.profiles.slice(0, 4).map((profile) => (
                  <div key={profile.user_id} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50/80 dark:bg-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group" onClick={() => navigate(`/dashboard/employees/${profile.user_id}`)} data-testid={`expiring-doc-${profile.user_id}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", profile.alerts.some(a => a.is_expired || a.days_remaining <= 30) ? "bg-rose-50 dark:bg-rose-900/20" : "bg-orange-50 dark:bg-orange-900/20")}>
                        <IdCard className={cn("h-3.5 w-3.5", profile.alerts.some(a => a.is_expired || a.days_remaining <= 30) ? "text-rose-500" : "text-orange-500")} />
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{profile.full_name}</span>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {profile.alerts.map((alert, idx) => (
                            <span key={idx} className={cn("text-[10px] px-1.5 py-0.5 rounded-full", alert.is_expired ? "bg-rose-50 text-rose-600" : alert.days_remaining <= 30 ? "bg-rose-50 text-rose-600" : "bg-orange-50 text-orange-600")}>
                              {alert.is_expired ? (t('employeeProfile.expired') || 'Süresi Doldu') : `${alert.days_remaining} ${t('employeeProfile.daysRemaining') || 'gün'}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2.5 p-3 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl" data-testid="all-docs-ok">
                <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <p className="text-xs text-emerald-700 dark:text-emerald-400">{t('dashboard.allDocsUpToDate')}</p>
              </div>
            )}
          </div>
          </DashCard>
        )}

        {/* ─── Bottom Charts ─── */}
        <DashCard id="leave-types" title={t('dashboard.topLeaveTypes')}>
        <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-5 h-full" data-testid="chart-leave-types">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('dashboard.topLeaveTypes')}</h3>
          <div className="h-48">
            {leaveTypeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={leaveTypeChartData} layout="vertical" barSize={14}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} width={90} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="approved_days" fill="#818CF8" radius={[0, 8, 8, 0]} name={t('dashboard.approvedDays')} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-400">{t('common.noData')}</div>
            )}
          </div>
        </div>
        </DashCard>

        <DashCard id="firm-compare" title={isAnyAdmin && dashboardData?.is_global ? t('dashboard.firmComparison') : t('dashboard.capacityHotspots')}>
        <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-5 h-full" data-testid="chart-firm-comparison">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{isAnyAdmin && dashboardData?.is_global ? t('dashboard.firmComparison') : t('dashboard.capacityHotspots')}</h3>
          {isAnyAdmin && dashboardData?.is_global && firmComparisonData.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={firmComparisonData} layout="vertical" barSize={14}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} width={90} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="approved_days" fill="#A78BFA" radius={[0, 8, 8, 0]} name={t('dashboard.approvedDays')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : dashboardData?.hotspots?.length > 0 ? (
            <div className="space-y-2">
              {dashboardData.hotspots.slice(0, 5).map((spot, index) => (
                <div key={spot.date} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50/80 dark:bg-slate-700/30">
                  <div className="flex items-center gap-2.5">
                    <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold", index === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500")}>{index + 1}</span>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{spot.date}</span>
                  </div>
                  <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full", spot.approved_count >= 5 ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600")}>{spot.approved_count} {t('dashboard.requests')}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-sm text-slate-400">{t('common.noData')}</div>
          )}
        </div>
        </DashCard>
      </DashboardCardBoard>

      {/* Status Users Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedStatus && DAILY_STATUS_CONFIG[selectedStatus] && (() => {
                const StatusIcon = DAILY_STATUS_CONFIG[selectedStatus].icon;
                return (
                  <>
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", DAILY_STATUS_CONFIG[selectedStatus].bgClass)}>
                      {StatusIcon && <StatusIcon className="h-4 w-4 text-white" />}
                    </div>
                    <span>{getStatusLabel(selectedStatus)}</span>
                    <Badge variant="secondary" className="ml-2">{modalUsers.length}</Badge>
                  </>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 max-h-[400px] overflow-y-auto">
            {modalUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500 dark:text-slate-400">
                  {t('dailyStatus.noUsers') }
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => {
                    setStatusModalOpen(false);
                    navigate('/dashboard/daily-status-v2');
                  }}
                >
                  {t('dailyStatus.goToPage') }
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {modalUsers.map((u) => (
                  <div 
                    key={u.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: DAILY_STATUS_CONFIG[selectedStatus]?.color || '#6B7280' }}
                    >
                      {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-100 truncate">{u.full_name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{u.email}</p>
                      {isAnyAdmin && u.firm_name && (
                        <p className="text-xs text-slate-400 truncate">{u.firm_name}</p>
                      )}
                    </div>
                  </div>
                ))}
                
                <div className="pt-3 mt-3 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      setStatusModalOpen(false);
                      navigate('/dashboard/daily-status-v2');
                    }}
                  >
                    {t('dailyStatus.goToPage') }
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
